import axios, { AxiosInstance, AxiosResponse } from 'axios';
import logger from '../utils/logger';

// SECURITY FIX: Environment-based API URL configuration
// Create React App uses process.env, not import.meta.env
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Log warning if using default URL in production
if (!process.env.REACT_APP_API_URL && process.env.NODE_ENV === 'production') {
  logger.warn('⚠️ WARNING: REACT_APP_API_URL not configured! Using default localhost URL.');
}

// Types for our API responses
export interface ApiResponse<T = any> {
  success: boolean;
  data: T;
  message: string;
  errors?: Record<string, string[]>;
  conflicts?: Array<{ date: string; error: string }>; // For recurring bookings
}

// Block conflict types for time slot blocking
export interface BlockConflictBooking {
  id: number;
  customer_name: string;
  customer_phone?: string;
  booking_reference: string;
  start_time: string;
  end_time: string;
  status: string;
  final_price: number;
  court_name: string;
}

export interface BlockConflictBlock {
  id: number;
  reason: string;
  customer_name?: string;
  start_time: string;
  end_time: string;
  court_name: string;
}

export interface BlockConflictDetails {
  has_conflicts: boolean;
  bookings: BlockConflictBooking[];
  blocks: BlockConflictBlock[];
  summary: {
    total_bookings: number;
    total_blocks: number;
    affected_courts: string[];
    total_revenue_at_risk: number;
  };
}

// Custom error class for block conflicts
export class BlockConflictError extends Error {
  conflicts: BlockConflictDetails;
  actionRequired: string;

  constructor(message: string, conflicts: BlockConflictDetails, actionRequired: string) {
    super(message);
    this.name = 'BlockConflictError';
    this.conflicts = conflicts;
    this.actionRequired = actionRequired;
  }
}

// Custom error class for court deactivation blocked
export interface BlockingBooking {
  id: number;
  reference: string;
  date?: string; // For one-time bookings
  day_of_week?: string; // For recurring bookings
  start_time: string;
  end_time: string;
  customer_name: string;
  status?: string;
  source?: string;
  frequency?: string; // For recurring bookings
  remaining_sessions?: number; // For recurring bookings
}

export interface DeactivationIssue {
  type: 'future_bookings' | 'recurring_bookings';
  count: number;
  message: string;
  bookings?: BlockingBooking[]; // First 5 bookings for display
}

export interface DeactivationBlockedDetails {
  issues: DeactivationIssue[];
  action_required: string;
}

export class DeactivationBlockedError extends Error {
  blockingReason: string;
  details: DeactivationBlockedDetails;

  constructor(message: string, blockingReason: string, details: DeactivationBlockedDetails) {
    super(message);
    this.name = 'DeactivationBlockedError';
    this.blockingReason = blockingReason;
    this.details = details;
  }
}

// Customer Profile types
export interface CustomerProfileBooking {
  id: number;
  booking_reference: string;
  booking_date: string;
  start_time: string;
  end_time: string;
  court_name: string;
  sport_name: string;
  amount: number;
  status: string;
  payment_status: string;
  payment_method?: string;
  source: 'spotts' | 'manual';
  recurring_group_id?: string | null;
  created_at: string;
}

export interface CustomerProfileStats {
  total_bookings: number;
  total_spent: number;
  avg_booking_value: number;
  cancelled_bookings: number;
  cancellation_rate: number;
  days_since_last_visit: number | null;
  last_visit_date: string | null;
  next_booking_date: string | null;
  source_breakdown: {
    spotts: number;
    manual: number;
  };
  payment_methods: Record<string, number>;
}

export interface CustomerProfilePatterns {
  preferred_day: string | null;
  preferred_time: string | null;
  preferred_court: string | null;
  preferred_sport: string | null;
  day_breakdown: Record<string, number>;
  time_breakdown: Record<string, number>;
}

export interface CustomerProfileMembership {
  id: number;
  package_name: string;
  price: number;
  venue_share: number;
  status: 'active' | 'expired' | 'cancelled';
  start_date: string;
  end_date: string;
  days_remaining: number;
  payment_status: string;
  source: string;
  created_at: string;
}

export interface CustomerProfileMembershipStats {
  total_memberships: number;
  total_membership_spend: number;
  has_active_membership: boolean;
  current_package: string | null;
  days_remaining: number | null;
}

export interface CustomerProfileData {
  customer: {
    name: string;
    email: string | null;
    phone: string | null;
    source: 'spotts' | 'manual';
    first_booking_date: string | null;
    last_booking_date: string | null;
  };
  stats: CustomerProfileStats;
  patterns: CustomerProfilePatterns;
  bookings: CustomerProfileBooking[];
  total_bookings_count: number;
  membership_stats?: CustomerProfileMembershipStats;
  memberships?: CustomerProfileMembership[];
}

export interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  phone?: string;
  bio?: string;
  venues: Venue[];
  must_change_password?: boolean;
  password_changed_at?: string;
}

export interface Venue {
  id: number;
  name: string;
  slug: string;
  description: string;
  address: string;
  city: string;
  state: string;
  country: string;
  phone: string;
  email: string;
  hourly_rate: string;
  daily_rate?: string;
  weekly_rate?: string;
  monthly_rate?: string;
  yearly_rate?: string;
  per_person_rate?: string;
  commission_rate?: number;
  gateway_fee_paid_by?: 'platform' | 'venue' | 'customer';
  refund_gateway_fee_paid_by?: 'venue' | 'customer' | 'platform';
  policy_editing_enabled?: boolean;
  status: string;
  images: string[];
  sports: Sport[];
  amenities: Amenity[];
  operating_hours: OperatingHour[];
  courts?: any[];
  membership_plans?: any[];
  special_offers?: any[];
  venue_type?: 'sports_facility' | 'gym' | 'multi_purpose' | 'outdoor_space';
  pricing_type?: 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'membership' | 'per_person';
  business_model?: 'single_sport' | 'multi_sport' | 'gym_sports_hybrid' | 'gym_only';
  has_gym_membership?: boolean;
  has_sports_booking?: boolean;
  gym_member_discount_percentage?: number;
  min_session_hours?: number;
  max_session_hours?: number | null;
  payment_mode?: 'manual_payout' | 'split_payment';
  subaccount_active?: boolean;
}

export interface Sport {
  id: number;
  name: string;
  slug: string;
}

export interface Amenity {
  id: number;
  name: string;
  slug: string;
}

export interface OperatingHour {
  id: number;
  day_of_week: string;
  opening_time: string;
  closing_time: string;
  is_closed: boolean;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
  phone: string;
  venue_name: string;
  venue_address: string;
  venue_city: string;
}

export interface AuthResponse {
  token: string;
  user: User;
  venues: Venue[];
  must_change_password?: boolean;
}

export interface ChangePasswordData {
  current_password: string;
  new_password: string;
  new_password_confirmation: string;
}

export interface ChangePasswordResponse {
  must_change_password: boolean;
  password_changed_at: string;
}

export interface DashboardStats {
  total_bookings: number;
  revenue: number;
  active_customers: number;
  monthly_bookings: number;
}

export interface RecentBooking {
  id: number;
  customer_name: string;
  time_slot: string;
  sport: string;
  status: string;
  amount: number;
}

export interface DashboardData {
  user: User;
  venues: Venue[];
  stats: DashboardStats;
  recent_bookings: RecentBooking[];
  notifications: {
    unread_messages: number;
    pending_bookings: number;
    urgent_notifications: number;
  };
}

class VendorApiService {
  private api: AxiosInstance;
  private token: string | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      withCredentials: true, // CRITICAL: Enable sending cookies with requests
    });

    // Request interceptor to add auth token and handle CSRF
    this.api.interceptors.request.use(
      async (config) => {
        // Add Bearer token if available (for non-cookie auth fallback)
        if (this.token) {
          config.headers.Authorization = `Bearer ${this.token}`;
        }

        // SECURITY FIX: Automatically fetch CSRF token for state-changing operations
        // Skip for GET/HEAD/OPTIONS requests (they don't modify state)
        const stateChangingMethods = ['POST', 'PUT', 'PATCH', 'DELETE'];
        if (config.method && stateChangingMethods.includes(config.method.toUpperCase())) {
          // Check if we already have CSRF cookie, if not, fetch it
          const hasCsrfCookie = document.cookie.includes('XSRF-TOKEN');
          if (!hasCsrfCookie) {
            try {
              // Remove /api from URL to get base backend URL
              const backendUrl = API_BASE_URL.replace('/api', '');
              await axios.get(`${backendUrl}/sanctum/csrf-cookie`, {
                withCredentials: true
              });
            } catch (error) {
              // Log but don't block the request
              logger.warn('Failed to fetch CSRF token:', error);
            }
          }

          // Extract XSRF token from cookie and add to headers
          const xsrfToken = this.getCookieValue('XSRF-TOKEN');
          if (xsrfToken) {
            config.headers['X-XSRF-TOKEN'] = decodeURIComponent(xsrfToken);
          }
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response: AxiosResponse<ApiResponse>) => {
        return response;
      },
      (error) => {
        if (error.response?.status === 401) {
          // Check if this is a login attempt - don't redirect for login failures
          // Login failures should show error message, not redirect
          const requestUrl = error.config?.url || '';
          const isLoginAttempt = requestUrl.includes('/vendor/login') ||
                                 requestUrl.includes('/v1/auth/');

          if (!isLoginAttempt) {
            // Only redirect for authenticated API calls that fail with 401
            // (token expired, session invalidated, etc.)
            this.clearToken();
            window.location.href = '/';
          }
        }

        // NOTE (staff-access): 403s are surfaced to the caller, NOT globally
        // redirected. A capability denial can come from a background prefetch
        // or poll the user never navigated to — an interceptor can't tell
        // those apart from a real navigation, so redirecting here caused an
        // app-wide bounce/reload storm. Forbidden-page handling for staff is
        // done at the router level (DashboardLayout), which knows the real
        // navigation target; individual pages show their own 403 state.
        return Promise.reject(error);
      }
    );

    // Load token from localStorage on initialization
    this.loadToken();
  }

  // Token management
  // SECURITY: We keep token in memory for the session to send as Bearer token
  // httpOnly cookie is a backup for session restoration
  setToken(token: string): void {
    this.token = token;
    // Store in sessionStorage (cleared when browser tab closes) instead of localStorage
    // This is more secure than localStorage but less secure than httpOnly cookies
    sessionStorage.setItem('vendor_token', token);
  }

  clearToken(): void {
    this.token = null;
    sessionStorage.removeItem('vendor_token');
    localStorage.removeItem('vendor_user');
  }

  private loadToken(): void {
    // Try to load from sessionStorage (current browser session only)
    const token = sessionStorage.getItem('vendor_token');
    if (token) {
      this.token = token;
    }
  }

  // Helper method to get cookie value by name
  private getCookieValue(name: string): string | null {
    const matches = document.cookie.match(new RegExp(
      '(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'
    ));
    return matches ? matches[1] : null;
  }

  isAuthenticated(): boolean {
    // Check if we have a token in memory or sessionStorage
    return !!this.token || !!sessionStorage.getItem('vendor_token');
  }

  // CSRF Protection: Get CSRF cookie from Laravel Sanctum
  async getCsrfCookie(): Promise<void> {
    try {
      // Laravel Sanctum endpoint to get CSRF cookie
      await axios.get(`${API_BASE_URL.replace('/api', '')}/sanctum/csrf-cookie`, {
        withCredentials: true
      });
    } catch (error) {
      logger.error('Failed to fetch CSRF cookie:', error);
      // Don't throw - allow request to proceed (will fail if CSRF is actually required)
    }
  }

  // Authentication endpoints
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    // SECURITY FIX: Get CSRF cookie before state-changing operation
    await this.getCsrfCookie();

    try {
      const response = await this.api.post<ApiResponse<AuthResponse>>('/vendor/login', credentials);

      if (response.data.success) {
        this.setToken(response.data.data.token);

        // Merge user and venues to ensure venues with sports are available
        // Also include must_change_password flag for first-login detection
        const userData = {
          ...response.data.data.user,
          venues: response.data.data.venues, // Use the venues array from response which has sports loaded
          must_change_password: response.data.data.must_change_password ?? false,
        };

        logger.log('🔐 Login successful - Storing user data:', {
          userId: userData.id,
          userName: userData.name,
          venuesCount: userData.venues?.length || 0,
          firstVenueSports: userData.venues?.[0]?.sports?.length || 0,
          mustChangePassword: userData.must_change_password,
        });

        localStorage.setItem('vendor_user', JSON.stringify(userData));
        return {
          ...response.data.data,
          must_change_password: response.data.data.must_change_password ?? false,
        };
      }

      // API returned success: false - create error with response for proper error handling
      const error = new Error(response.data.message || 'Login failed') as any;
      error.response = { data: response.data, status: 200 };
      throw error;
    } catch (error: any) {
      // Re-throw axios errors as-is to preserve response data for Login component
      // This allows Login.tsx to extract specific error messages like "Invalid credentials"
      throw error;
    }
  }

  async register(data: RegisterData): Promise<AuthResponse> {
    // SECURITY FIX: Get CSRF cookie before state-changing operation
    await this.getCsrfCookie();

    const response = await this.api.post<ApiResponse<AuthResponse>>('/vendor/register', data);

    if (response.data.success) {
      this.setToken(response.data.data.token);
      localStorage.setItem('vendor_user', JSON.stringify(response.data.data.user));
      return response.data.data;
    }

    throw new Error(response.data.message || 'Registration failed');
  }

  async logout(): Promise<void> {
    try {
      // SECURITY FIX: Get CSRF cookie before state-changing operation
      await this.getCsrfCookie();
      await this.api.post<ApiResponse>('/vendor/logout');
    } catch (error) {
      // Continue with logout even if API call fails
      logger.error('Logout API call failed:', error);
    } finally {
      this.clearToken();
    }
  }

  /**
   * Change password - used for first login forced password change
   * and regular password changes from settings
   */
  async changePassword(data: ChangePasswordData): Promise<ChangePasswordResponse> {
    await this.getCsrfCookie();

    const response = await this.api.put<ApiResponse<ChangePasswordResponse>>('/vendor/change-password', data);

    if (response.data.success) {
      // Update stored user to reflect password change
      const storedUser = this.getStoredUser();
      if (storedUser) {
        storedUser.must_change_password = false;
        storedUser.password_changed_at = response.data.data.password_changed_at;
        localStorage.setItem('vendor_user', JSON.stringify(storedUser));
      }

      logger.log('🔐 Password changed successfully');
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to change password');
  }

  // ============================================================================
  // Password Reset Flow (3 steps)
  // ============================================================================

  // Auth response type (backend uses 'status' instead of 'success')
  private isAuthSuccess(data: { status?: string; success?: boolean }): boolean {
    return data.status === 'success' || data.success === true;
  }

  /**
   * Step 1: Request password reset code
   * Sends a 6-digit code to the user's email
   */
  async forgotPassword(email: string): Promise<{ message: string }> {
    await this.getCsrfCookie();

    const response = await this.api.post<{ status: string; message: string }>('/v1/auth/forgot-password', { email });

    if (this.isAuthSuccess(response.data)) {
      return { message: response.data.message || 'Password reset code sent to your email.' };
    }

    throw new Error(response.data.message || 'Failed to send reset code');
  }

  /**
   * Step 2: Verify the reset code
   * Confirms the code is valid before allowing password reset
   */
  async verifyResetCode(email: string, code: string): Promise<{ message: string }> {
    await this.getCsrfCookie();

    const response = await this.api.post<{ status: string; message: string }>('/v1/auth/verify-reset-code', { email, code });

    if (this.isAuthSuccess(response.data)) {
      return { message: response.data.message || 'Code verified successfully.' };
    }

    throw new Error(response.data.message || 'Invalid or expired code');
  }

  /**
   * Step 3: Reset password with verified code
   * Sets a new password after code verification
   */
  async resetPassword(
    email: string,
    code: string,
    password: string,
    password_confirmation: string
  ): Promise<{ message: string }> {
    await this.getCsrfCookie();

    const response = await this.api.post<{ status: string; message: string }>('/v1/auth/reset-password', {
      email,
      code,
      password,
      password_confirmation,
    });

    if (this.isAuthSuccess(response.data)) {
      return { message: response.data.message || 'Password reset successful.' };
    }

    throw new Error(response.data.message || 'Failed to reset password');
  }

  async getProfile(): Promise<User> {
    const response = await this.api.get<ApiResponse<User>>('/vendor/profile');

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to fetch profile');
  }

  async updateProfile(data: Partial<User>): Promise<User> {
    const response = await this.api.put<ApiResponse<User>>('/vendor/profile', data);

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to update profile');
  }

  // Dashboard data
  async getDashboard(): Promise<DashboardData> {
    const response = await this.api.get<ApiResponse<DashboardData>>('/vendor/dashboard');

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to fetch dashboard data');
  }

  // Venue management
  async getMyVenues(): Promise<Venue[]> {
    const response = await this.api.get<any>('/vendor/my-venues');

    // Backend returns { status: 'success', data: {...pagination}, message: '' }
    if (response.data.status === 'success' || response.data.success) {
      // Handle paginated response
      const paginatedData = response.data.data;
      const venues = paginatedData?.data || paginatedData || [];
      return Array.isArray(venues) ? venues : [];
    }

    throw new Error(response.data.message || 'Failed to fetch venues');
  }

  // Operating hours management
  async updateOperatingHours(venueId: number, operatingHours: Array<{
    day_of_week: string;
    is_closed: boolean;
    opening_time?: string | null;
    closing_time?: string | null;
  }>): Promise<OperatingHour[]> {
    const response = await this.api.put<ApiResponse<OperatingHour[]>>(
      `/vendor/venues/${venueId}/operating-hours`,
      { operating_hours: operatingHours }
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to update operating hours');
  }

  // updatePricing removed Jul 2026: its backend endpoint was dead-broken
  // since shipping and is deleted (D27). Use updateVenuePricing below.

  // Update venue pricing with discounts (for mobile app integration)
  async updateVenuePricing(venueId: number, pricingData: any): Promise<any> {
    const response = await this.api.put<ApiResponse<any>>(
      `/vendor/venues/${venueId}`,
      pricingData
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to update venue pricing');
  }

  async updateVenueCapacity(venueId: number, sessionData: { min_session_hours: number; max_session_hours: number | null }): Promise<any> {
    const response = await this.api.put<ApiResponse<any>>(
      `/vendor/venues/${venueId}/capacity`,
      sessionData
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to update session duration settings');
  }

  // Blackout dates
  async getBlackoutDates(venueId: number): Promise<Array<{ date: string; reason: string; id?: number }>> {
    const response = await this.api.get<ApiResponse<Array<{ date: string; reason: string; id?: number }>>>(
      `/vendor/venues/${venueId}/blackout-dates`
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to fetch blackout dates');
  }

  async addBlackoutDate(venueId: number, date: string, reason: string, sportId?: number | null): Promise<any> {
    const payload: any = { date, reason };
    if (sportId !== undefined && sportId !== null) {
      payload.sport_id = sportId;
    }

    const response = await this.api.post<ApiResponse<any>>(
      `/vendor/venues/${venueId}/blackout-dates`,
      payload
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to add blackout date');
  }

  async removeBlackoutDate(venueId: number, blackoutId: number): Promise<any> {
    const response = await this.api.delete<ApiResponse<any>>(
      `/vendor/venues/${venueId}/blackout-dates/${blackoutId}`
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to remove blackout date');
  }

  // Group Discounts
  async getGroupDiscounts(venueId: number): Promise<Array<{ id: string; title: string; description: string; discount_percentage: number; condition: string; valid_days?: string[] }>> {
    const response = await this.api.get<ApiResponse<Array<{ id: string; title: string; description: string; discount_percentage: number; condition: string; valid_days?: string[] }>>>(
      `/vendor/venues/${venueId}/group-discounts`
    );

    if (response.data.success) {
      // Ensure we always return an array, even if the API returns null/undefined
      const data = response.data.data;
      return Array.isArray(data) ? data : [];
    }

    throw new Error(response.data.message || 'Failed to fetch group discounts');
  }

  async addGroupDiscount(venueId: number, discount: {
    title: string;
    description: string;
    discount_percentage: number;
    condition: string;
    valid_days?: string[];
    sport_id?: number | null;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>(
      `/vendor/venues/${venueId}/group-discounts`,
      discount
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to add group discount');
  }

  async removeGroupDiscount(venueId: number, discountId: string): Promise<any> {
    const response = await this.api.delete<ApiResponse<any>>(
      `/vendor/venues/${venueId}/group-discounts/${discountId}`
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to remove group discount');
  }

  // Special Offers
  async getSpecialOffers(venueId: number): Promise<Array<{ id: string; title: string; description: string; discount_percentage?: number; discount_amount?: number; start_date: string; end_date: string; terms?: string; is_active: boolean }>> {
    const response = await this.api.get<ApiResponse<Array<{ id: string; title: string; description: string; discount_percentage?: number; discount_amount?: number; start_date: string; end_date: string; terms?: string; is_active: boolean }>>>(
      `/vendor/venues/${venueId}/special-offers`
    );

    if (response.data.success) {
      // Ensure we always return an array, even if the API returns null/undefined
      const data = response.data.data;
      return Array.isArray(data) ? data : [];
    }

    throw new Error(response.data.message || 'Failed to fetch special offers');
  }

  async addSpecialOffer(venueId: number, offer: {
    title: string;
    description: string;
    discount_percentage?: number;
    discount_amount?: number;
    start_date: string;
    end_date: string;
    terms?: string;
    is_active?: boolean;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>(
      `/vendor/venues/${venueId}/special-offers`,
      offer
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to add special offer');
  }

  async removeSpecialOffer(venueId: number, offerId: string): Promise<any> {
    const response = await this.api.delete<ApiResponse<any>>(
      `/vendor/venues/${venueId}/special-offers/${offerId}`
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to remove special offer');
  }

  // Promotions
  async getPromotions(venueId: number): Promise<any[]> {
    const response = await this.api.get<ApiResponse<any[]>>(`/vendor/venues/${venueId}/promotions`);

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to fetch promotions');
  }

  async createPromotion(venueId: number, data: {
    title: string;
    description?: string;
    discount_percentage: number;
    days_of_week?: string[];
    start_time?: string;
    end_time?: string;
    valid_from: string;
    valid_until: string;
    sport_id?: number;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>(`/vendor/venues/${venueId}/promotions`, data);

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to create promotion');
  }

  async updatePromotion(venueId: number, promotionId: number, data: {
    title?: string;
    description?: string;
    discount_percentage?: number;
    days_of_week?: string[];
    start_time?: string;
    end_time?: string;
    valid_from?: string;
    valid_until?: string;
    sport_id?: number;
  }): Promise<any> {
    const response = await this.api.put<ApiResponse<any>>(`/vendor/venues/${venueId}/promotions/${promotionId}`, data);

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to update promotion');
  }

  async togglePromotion(venueId: number, promotionId: number): Promise<any> {
    const response = await this.api.patch<ApiResponse<any>>(`/vendor/venues/${venueId}/promotions/${promotionId}/toggle`);

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to toggle promotion');
  }

  async deletePromotion(venueId: number, promotionId: number): Promise<void> {
    const response = await this.api.delete<ApiResponse<void>>(`/vendor/venues/${venueId}/promotions/${promotionId}`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete promotion');
    }
  }

  // Old JSON-field membership-plan API removed Jul 2026 (backend D29) —
  // the Memberships page uses the packages system (MembershipPackageController).

  // ==================== COURTS/FIELDS MANAGEMENT ====================

  /**
   * Get all courts/fields for a venue
   */
  async getCourts(venueId: number): Promise<any[]> {
    const response = await this.api.get<ApiResponse<any[]>>(`/vendor/venues/${venueId}/courts`);

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to fetch courts');
  }

  /**
   * Get today's schedule for a specific court
   */
  async getCourtTodaySchedule(venueId: number, courtId: number): Promise<{
    court: any;
    schedule: {
      total_slots: number;
      available: number;
      booked: number;
      blocked: number;
      unavailable: number;
      slots: Array<{
        time: string;
        start_time: string;
        end_time: string;
        status: 'available' | 'booked' | 'blocked' | 'unavailable';
        reference?: string | null;
        reason?: string | null;
        block_id?: number | null;
        partner_id?: number | null;
        partner_name?: string | null;
        is_partner_block?: boolean;
      }>;
    };
  }> {
    const response = await this.api.get<ApiResponse<any>>(
      `/vendor/venues/${venueId}/courts/${courtId}/today-schedule`
    );

    if (response.data.success) {
      const data = response.data.data;

      // Map nested partner object to flat fields for frontend consumption
      if (data.schedule?.slots) {
        data.schedule.slots = data.schedule.slots.map((slot: any) => ({
          ...slot,
          partner_id: slot.partner?.partner_id || slot.partner_id || null,
          partner_name: slot.partner?.partner_name || slot.partner_name || null,
          is_partner_block: !!(slot.partner?.partner_id || slot.partner_id),
        }));
      }

      return data;
    }

    throw new Error(response.data.message || 'Failed to fetch court schedule');
  }

  /**
   * Add a new court/field
   */
  async addCourt(venueId: number, courtData: {
    name: string;
    sport_id: number;
    court_number?: number;
    description?: string;
    surface_type?: string;
    capacity?: number;
    custom_hourly_rate?: number;
    is_indoor?: boolean;
    is_active?: boolean;
    min_booking_duration_hours?: number | null;
    booking_slot_increment_hours?: number | null;
    [key: string]: any;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>(
      `/vendor/venues/${venueId}/courts`,
      courtData
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to add court');
  }

  /**
   * Update a court/field
   */
  async updateCourt(venueId: number, courtId: number, courtData: {
    name?: string;
    sport_id?: number;
    court_number?: number;
    description?: string;
    surface_type?: string;
    capacity?: number;
    custom_hourly_rate?: number;
    is_indoor?: boolean;
    is_active?: boolean;
    min_booking_duration_hours?: number | null;
    booking_slot_increment_hours?: number | null;
    [key: string]: any;
  }): Promise<any> {
    try {
      const response = await this.api.put<ApiResponse<any>>(
        `/vendor/venues/${venueId}/courts/${courtId}`,
        courtData
      );

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to update court');
    } catch (error: any) {
      // Handle 422 Unprocessable Entity for deactivation blocked
      if (error.response?.status === 422) {
        const responseData = error.response.data;

        // Check if this is a deactivation blocked error
        if (responseData.blocking_reason === 'existing_bookings' && responseData.details) {
          throw new DeactivationBlockedError(
            responseData.message || 'Cannot deactivate court',
            responseData.blocking_reason,
            responseData.details
          );
        }
      }

      // Re-throw other errors with message
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  }

  /**
   * Delete a court/field
   */
  async deleteCourt(venueId: number, courtId: number): Promise<void> {
    const response = await this.api.delete<ApiResponse<void>>(
      `/vendor/venues/${venueId}/courts/${courtId}`
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete court');
    }
  }

  // ============================================================================
  // BOOKING MANAGEMENT METHODS (Phase 2)
  // ============================================================================

  /**
   * Get all bookings for a venue
   */
  async getBookings(venueId: number, filters?: {
    start_date?: string;
    end_date?: string;
    court_id?: number;
    status?: string;
    payment_status?: string;
  }): Promise<any[]> {
    const params = new URLSearchParams();

    if (filters) {
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.court_id) params.append('court_id', String(filters.court_id));
      if (filters.status) params.append('status', filters.status);
      if (filters.payment_status) params.append('payment_status', filters.payment_status);
    }

    const response = await this.api.get<ApiResponse<any[]>>(
      `/vendor/venues/${venueId}/bookings?${params.toString()}`
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to fetch bookings');
  }

  /**
   * Get all recurring bookings for a venue
   */
  async getRecurringBookings(venueId: number, filters?: {
    status?: string;
  }): Promise<any[]> {
    const params = new URLSearchParams();

    if (filters?.status) {
      params.append('status', filters.status);
    }

    const response = await this.api.get<ApiResponse<any[]>>(
      `/vendor/venues/${venueId}/recurring-bookings?${params.toString()}`
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to fetch recurring bookings');
  }

  /**
   * Get combined schedule (bookings + recurring instances + blocked slots)
   */
  async getSchedule(venueId: number, filters?: {
    date?: string;
    start_date?: string;
    end_date?: string;
    court_id?: number;
  }): Promise<any> {
    const params = new URLSearchParams();

    if (filters) {
      if (filters.date) params.append('date', filters.date);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.court_id) params.append('court_id', String(filters.court_id));
    }

    const response = await this.api.get<ApiResponse<any>>(
      `/vendor/venues/${venueId}/schedule?${params.toString()}`
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to fetch schedule');
  }

  /**
   * Check in a one-time booking
   */
  async checkInBooking(venueId: number, bookingId: number): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>(
      `/vendor/venues/${venueId}/bookings/${bookingId}/check-in`
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to check in booking');
  }

  /**
   * Check in a recurring booking instance
   */
  async checkInRecurringInstance(venueId: number, instanceId: number): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>(
      `/vendor/venues/${venueId}/recurring-instances/${instanceId}/check-in`
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to check in recurring instance');
  }

  /**
   * Check in a walk-in from blocked slot
   */
  async checkInWalkIn(venueId: number, blockId: number, customerName?: string): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>(
      `/vendor/venues/${venueId}/blocked-slots/${blockId}/check-in`,
      { customer_name: customerName }
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to check in walk-in');
  }

  /**
   * QR scan check-in — auto-resolves venue from the booking/membership.
   * Called after parsing the QR code payload from the customer's app.
   * The backend validates that the booking/membership belongs to one of the vendor's venues.
   */
  async scanCheckIn(payload: { type: string; id: number; instance_id?: number }): Promise<any> {
    let url: string;
    if (payload.type === 'membership') {
      url = `/vendor/scan/memberships/${payload.id}/check-in`;
    } else if (payload.type === 'recurring' && payload.instance_id) {
      url = `/vendor/scan/recurring-instances/${payload.instance_id}/check-in`;
    } else {
      url = `/vendor/scan/bookings/${payload.id}/check-in`;
    }

    const response = await this.api.post<ApiResponse<any>>(url);

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Check-in failed');
  }

  /**
   * Block a time slot (supports single day or multi-day range)
   * @throws {BlockConflictError} When there are existing bookings/blocks that conflict
   * @throws {Error} For other errors
   */
  async blockTimeSlot(venueId: number, courtId: number, data: {
    date: string;
    end_date?: string; // Optional: for multi-day blocking
    start_time: string;
    end_time: string;
    reason: string;
    customer_name?: string;
    customer_phone?: string;
    notes?: string;
  }): Promise<any> {
    try {
      const response = await this.api.post<ApiResponse<any>>(
        `/vendor/venues/${venueId}/courts/${courtId}/block-slot`,
        data
      );

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error(response.data.message || 'Failed to block time slot');
    } catch (error: any) {
      // Handle 409 Conflict response with detailed conflict info
      if (error.response?.status === 409) {
        const responseData = error.response.data;
        const conflicts = responseData.conflicts as BlockConflictDetails;
        const actionRequired = responseData.action_required || 'Please cancel conflicting bookings first';

        throw new BlockConflictError(
          responseData.message || 'Cannot block - conflicts exist',
          conflicts,
          actionRequired
        );
      }

      // Re-throw other errors
      throw error;
    }
  }

  /**
   * Unblock a time slot
   */
  async unblockTimeSlot(venueId: number, courtId: number, blockId: number): Promise<any> {
    const response = await this.api.delete<ApiResponse<any>>(
      `/vendor/venues/${venueId}/courts/${courtId}/block-slot/${blockId}`
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to unblock time slot');
  }

  /**
   * Update a blocked slot
   */
  async updateBlockedSlot(venueId: number, courtId: number, blockId: number, data: {
    reason?: string;
    customer_name?: string;
    customer_phone?: string;
    notes?: string;
  }): Promise<any> {
    const response = await this.api.put<ApiResponse<any>>(
      `/vendor/venues/${venueId}/courts/${courtId}/block-slot/${blockId}`,
      data
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to update blocked slot');
  }

  // ==================== MANUAL BOOKINGS ====================

  /**
   * Get existing customers for autocomplete (both Spotts users and manual booking customers)
   */
  async getManualBookingCustomers(venueId: number): Promise<Array<{ name: string; phone: string | null; email: string | null; source: 'spotts' | 'manual' }>> {
    const response = await this.api.get<ApiResponse<{ customers: Array<{ name: string; phone: string | null; email: string | null; source: 'spotts' | 'manual' }> }>>(
      `/vendor/venues/${venueId}/manual-bookings/customers`,
      { params: { venue_id: venueId } }
    );

    return response.data.data?.customers || [];
  }

  /**
   * Get existing membership customers for autocomplete (both Spotts users and manual members)
   */
  async getMembershipCustomers(venueId: number): Promise<Array<{ name: string; phone: string | null; email: string | null; source: 'spotts' | 'manual' }>> {
    const response = await this.api.get<ApiResponse<{ customers: Array<{ name: string; phone: string | null; email: string | null; source: 'spotts' | 'manual' }> }>>(
      `/vendor/venues/${venueId}/membership-customers`,
      { params: { venue_id: venueId } }
    );

    return response.data.data?.customers || [];
  }

  /**
   * Get detailed customer profile with booking history and insights
   */
  async getCustomerProfile(venueId: number, params: {
    name: string;
    email?: string;
    phone?: string;
    source: 'spotts' | 'manual';
  }): Promise<CustomerProfileData> {
    const response = await this.api.get<ApiResponse<CustomerProfileData>>(
      `/vendor/venues/${venueId}/customers/profile`,
      { params }
    );
    return response.data.data;
  }

  /**
   * Create a manual booking (walk-in, phone booking, etc.)
   */
  async createManualBooking(venueId: number, bookingData: {
    court_id: number;
    booking_date: string;
    start_time: string;
    end_time: string;
    customer_name: string;
    customer_phone?: string; // Optional - not all walk-ins provide phone
    price: number;
    payment_method: 'cash' | 'transfer' | 'pos';
    number_of_players?: number;
    notes?: string;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>(
      `/vendor/venues/${venueId}/manual-bookings`,
      bookingData
    );

    if (response.data.success) {
      return response.data;
    }

    throw new Error(response.data.message || 'Failed to create manual booking');
  }

  /**
   * Create recurring manual bookings (multiple sessions with flexible per-session settings)
   */
  async createRecurringManualBookings(venueId: number, bookingData: {
    sessions: Array<{
      date: string;
      start_time: string;
      end_time: string;
      court_id: number;
      price: number;
    }>;
    customer_name: string;
    customer_phone?: string;
    payment_method: 'cash' | 'transfer' | 'pos';
    number_of_players?: number;
    notes?: string;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>(
      `/vendor/venues/${venueId}/manual-bookings/recurring`,
      bookingData
    );

    if (response.data.success) {
      return response.data;
    }

    // If there are conflicts, throw error with conflict details
    if (response.data.conflicts && response.data.conflicts.length > 0) {
      const conflictDetails = response.data.conflicts
        .map((c: any) => `Session ${c.session} (${c.date}, ${c.time}): ${c.error}`)
        .join('\n');
      throw new Error(response.data.message + '\n\nConflicts:\n' + conflictDetails);
    }

    throw new Error(response.data.message || 'Failed to create recurring manual bookings');
  }

  /**
   * Edit price, notes, and/or customer info on a manual booking session
   */
  async updateManualBooking(
    venueId: number,
    bookingId: number,
    data: {
      price?: number;
      notes?: string;
      customer_name?: string;
      customer_phone?: string;
      customer_email?: string;
      notify_customer?: boolean;
    }
  ): Promise<{ booking: any; message: string }> {
    try {
      const response = await this.api.patch<ApiResponse<{ booking: any }>>(
        `/vendor/venues/${venueId}/manual-bookings/${bookingId}`,
        data
      );
      if (response.data.success) {
        return { booking: response.data.data?.booking, message: response.data.message || 'Updated' };
      }
      throw new Error(response.data.message || 'Failed to update booking');
    } catch (err: any) {
      // Surface the real backend validation message instead of generic Axios "422" text
      const message = err.response?.data?.message || err.message || 'Failed to update booking';
      throw new Error(message);
    }
  }

  /**
   * Bulk-update price on non-cancelled sessions in a recurring group.
   * include_past=true also updates past sessions; false (default) = upcoming only.
   */
  async bulkUpdateGroupPrice(
    venueId: number,
    groupId: string,
    data: { price: number; notes: string; include_past?: boolean }
  ): Promise<{ sessions_updated: number; message: string }> {
    try {
      const response = await this.api.patch<ApiResponse<{ sessions_updated: number }>>(
        `/vendor/venues/${venueId}/manual-bookings/recurring-group/${groupId}/price`,
        data
      );
      if (response.data.success) {
        return {
          sessions_updated: response.data.data?.sessions_updated ?? 0,
          message: response.data.message || 'Updated',
        };
      }
      throw new Error(response.data.message || 'Failed to update group price');
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to update group price';
      throw new Error(message);
    }
  }

  /**
   * Cancel a manual booking with optional refund tracking
   */
  async cancelManualBooking(
    venueId: number,
    bookingId: number,
    reason: string,
    refundData?: { refund_type: 'none' | 'partial' | 'full'; refund_amount?: number },
    notifyCustomer?: boolean
  ): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>(
      `/vendor/venues/${venueId}/manual-bookings/${bookingId}/cancel`,
      {
        reason,
        notify_customer: notifyCustomer ?? false,
        ...(refundData && {
          refund_type: refundData.refund_type,
          ...(refundData.refund_type === 'partial' && { refund_amount: refundData.refund_amount }),
        }),
      }
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to cancel manual booking');
  }

  /**
   * Delete a manual booking entirely (for correcting mistakes)
   * Only works for manual bookings, not Spotts app bookings
   */
  async deleteManualBooking(venueId: number, bookingId: number): Promise<void> {
    const response = await this.api.delete<ApiResponse<void>>(
      `/vendor/venues/${venueId}/manual-bookings/${bookingId}`
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete manual booking');
    }
  }

  /**
   * Cancel all upcoming sessions in a manual recurring booking group
   */
  async cancelRecurringGroup(
    venueId: number,
    groupId: string,
    reason: string,
    refundData?: { refund_type: 'none' | 'partial' | 'full'; refund_amount?: number },
    notifyCustomer?: boolean
  ): Promise<{
    cancelled_count: number;
    already_cancelled_count: number;
    past_count: number;
    total_sessions: number;
    message: string;
  }> {
    const response = await this.api.post<ApiResponse<{
      cancelled_count: number;
      already_cancelled_count: number;
      past_count: number;
      total_sessions: number;
      message: string;
    }>>(
      `/vendor/venues/${venueId}/manual-bookings/recurring-group/${groupId}/cancel`,
      {
        reason,
        notify_customer: notifyCustomer ?? false,
        ...(refundData && {
          refund_type: refundData.refund_type,
          ...(refundData.refund_type === 'partial' && { refund_amount: refundData.refund_amount }),
        }),
      }
    );

    if (response.data.success) {
      return response.data as any;
    }

    throw new Error(response.data.message || 'Failed to cancel recurring group');
  }

  /**
   * Delete an entire manual recurring booking group (all sessions must be cancelled)
   */
  async deleteRecurringGroup(venueId: number, groupId: string): Promise<{
    deleted_count: number;
    message: string;
  }> {
    const response = await this.api.delete<ApiResponse<{
      deleted_count: number;
      message: string;
    }>>(
      `/vendor/venues/${venueId}/manual-bookings/recurring-group/${groupId}`
    );

    if (response.data.success) {
      return response.data as any;
    }

    throw new Error(response.data.message || 'Failed to delete recurring group');
  }

  /**
   * Reschedule a one-time booking
   */
  async rescheduleBooking(venueId: number, bookingId: number, data: {
    new_date: string;
    new_start_time: string;
    new_end_time: string;
    reason?: string;
    notify_customer: boolean;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>(
      `/vendor/venues/${venueId}/bookings/${bookingId}/reschedule`,
      data
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to reschedule booking');
  }

  /**
   * Reschedule a recurring booking instance
   */
  async rescheduleRecurringInstance(venueId: number, instanceId: number, data: {
    new_date: string;
    new_start_time: string;
    new_end_time: string;
    reason?: string;
    notify_customer: boolean;
  }): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>(
      `/vendor/venues/${venueId}/recurring-instances/${instanceId}/reschedule`,
      data
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to reschedule session');
  }

  /**
   * Cancel a single recurring booking instance (Phase 9)
   */
  async cancelRecurringInstance(venueId: number, instanceId: number, reason: string): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>(
      `/vendor/venues/${venueId}/recurring-instances/${instanceId}/cancel`,
      { reason }
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to cancel session');
  }

  /**
   * Cancel all future sessions of a recurring booking with refund calculation (Phase 9)
   */
  async cancelFutureRecurringSessions(venueId: number, recurringBookingId: number, reason: string): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>(
      `/vendor/venues/${venueId}/recurring-bookings/${recurringBookingId}/cancel-future`,
      { reason }
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to cancel future sessions');
  }

  /**
   * Cancel the entire recurring booking plan (Phase 9)
   */
  async cancelEntireRecurringBooking(venueId: number, recurringBookingId: number, reason: string): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>(
      `/vendor/venues/${venueId}/recurring-bookings/${recurringBookingId}/cancel-all`,
      { reason }
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to cancel entire booking');
  }

  /**
   * Get refund estimate for a booking before cancellation
   */
  async getRefundEstimate(bookingId: number): Promise<any> {
    const response = await this.api.get<ApiResponse<any>>(
      `/bookings/${bookingId}/refund-estimate`
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to fetch refund estimate');
  }

  /**
   * Mark whether the vendor manually refunded the customer for a split payment cancellation.
   */
  async markSplitRefundGiven(venueId: number, bookingId: number, given: boolean): Promise<any> {
    const response = await this.api.patch<ApiResponse<any>>(
      `/vendor/venues/${venueId}/bookings/${bookingId}/split-refund-given`,
      { given }
    );
    if (response.data.success) return response.data;
    throw new Error(response.data.message || 'Failed to update refund status');
  }

  /**
   * Cancel a one-time booking (Phase 9E)
   */
  async cancelBooking(venueId: number, bookingId: number, reason: string, splitRefundGiven?: boolean, splitRefundAmount?: number): Promise<any> {
    logger.log('🌐 [API] Cancelling booking:', { venueId, bookingId, reason, splitRefundGiven, splitRefundAmount });

    const body: Record<string, unknown> = { reason };
    if (splitRefundGiven !== undefined) {
      body.split_refund_given = splitRefundGiven;
    }
    if (splitRefundAmount !== undefined) {
      body.split_refund_amount = splitRefundAmount;
    }

    const response = await this.api.post<ApiResponse<any>>(
      `/vendor/venues/${venueId}/bookings/${bookingId}/cancel`,
      body
    );

    logger.log('🌐 [API] Cancel response:', response.data);

    if (response.data.success) {
      logger.log('🌐 [API] Booking data from response:', response.data.data.booking);
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to cancel booking');
  }

  /**
   * Check availability for a time slot
   */
  async checkAvailability(venueId: number, courtId: number, date: string, startTime: string, endTime: string): Promise<boolean> {
    try {
      // Use the existing schedule endpoint to check for conflicts
      const response = await this.api.get<ApiResponse<any>>(
        `/vendor/venues/${venueId}/schedule`,
        {
          params: {
            start_date: date,
            end_date: date,
            court_id: courtId,
          }
        }
      );

      if (!response.data.success) {
        return false;
      }

      // Check for any overlapping bookings or blocked slots
      const schedule = response.data.data;

      // Check one-time bookings
      const hasConflictingBooking = schedule.bookings?.some((booking: any) => {
        if (booking.court_id !== courtId || booking.booking_date !== date) {
          return false;
        }

        const bookingStart = booking.start_time;
        const bookingEnd = booking.end_time;

        // Check for overlap
        return (
          (startTime >= bookingStart && startTime < bookingEnd) ||
          (endTime > bookingStart && endTime <= bookingEnd) ||
          (startTime <= bookingStart && endTime >= bookingEnd)
        );
      });

      if (hasConflictingBooking) {
        return false;
      }

      // Check recurring instances
      const hasConflictingInstance = schedule.recurring_instances?.some((instance: any) => {
        if (instance.scheduled_date !== date) {
          return false;
        }

        // Need to get court_id from the recurring booking - assuming it's available
        const instanceStart = instance.scheduled_start_time;
        const instanceEnd = instance.scheduled_end_time;

        // Check for overlap
        return (
          (startTime >= instanceStart && startTime < instanceEnd) ||
          (endTime > instanceStart && endTime <= instanceEnd) ||
          (startTime <= instanceStart && endTime >= instanceEnd)
        );
      });

      if (hasConflictingInstance) {
        return false;
      }

      // Check blocked slots
      const hasConflictingBlock = schedule.blocked_slots?.some((block: any) => {
        if (block.court_id !== courtId || block.date !== date) {
          return false;
        }

        const blockStart = block.start_time;
        const blockEnd = block.end_time;

        // Check for overlap
        return (
          (startTime >= blockStart && startTime < blockEnd) ||
          (endTime > blockStart && endTime <= blockEnd) ||
          (startTime <= blockStart && endTime >= blockEnd)
        );
      });

      if (hasConflictingBlock) {
        return false;
      }

      // No conflicts found - slot is available
      return true;
    } catch (error) {
      logger.error('Availability check error:', error);
      return false;
    }
  }

  // ============================================================================
  // BANK DETAILS & SPLIT PAYMENTS (Phase 1)
  // ============================================================================

  /**
   * Get vendor's bank details
   */
  async getBankDetails(): Promise<{
    bank_name: string | null;
    bank_code: string | null;
    bank_account_number_masked: string | null;
    bank_account_name: string | null;
    bank_details_verified: boolean;
    bank_details_verified_at: string | null;
    has_bank_details: boolean;
  }> {
    const response = await this.api.get<ApiResponse<any>>('/vendor/bank-details');

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to fetch bank details');
  }

  /**
   * Update vendor's bank details
   */
  async updateBankDetails(data: {
    bank_name: string;
    bank_code: string;
    bank_account_number: string;
    bank_account_name: string;
  }): Promise<{
    bank_name: string;
    bank_code: string;
    bank_account_number: string; // Masked
    bank_account_name: string;
    bank_details_verified: boolean;
  }> {
    const response = await this.api.put<ApiResponse<any>>('/vendor/bank-details', data);

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to update bank details');
  }

  /**
   * Create Paystack subaccount for a venue
   */
  async createSubaccount(venueId: number): Promise<{
    subaccount_code: string;
    message: string;
  }> {
    const response = await this.api.post<ApiResponse<any>>(
      `/vendor/venues/${venueId}/create-subaccount`
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to create subaccount');
  }

  /**
   * Get subaccount details from Paystack
   */
  async getSubaccountDetails(venueId: number): Promise<any> {
    const response = await this.api.get<ApiResponse<any>>(
      `/vendor/venues/${venueId}/subaccount-details`
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to fetch subaccount details');
  }

  /**
   * Get earnings summary with 85/15 split
   */
  async getEarningsSummary(venueId: number, source: 'all' | 'spotts' | 'manual' = 'all'): Promise<{
    total_revenue: number;
    vendor_earnings: number;
    platform_commission: number;
    vendor_share_percentage: number;
    platform_share_percentage: number;
    outstanding_debt: number;
    net_earnings: number;
    total_refunds: number;
    refund_count: number;
    has_subaccount: boolean;
    subaccount_code: string | null;
    payout: {
      available_balance: number;
      held_balance: number;
      pending_debt: number;
      reserved_for_refunds: number;
      reserved_refunds_count: number;
      eligible_bookings_count: number;
      held_bookings_count: number;
      next_payout_date: string | null;
      payout_day: string;
      meets_minimum: boolean;
      minimum_threshold: number;
      history: Array<{
        id: number;
        transfer_reference: string;
        net_payout: number;
        gross_revenue: number;
        platform_commission: number;
        refunds_deducted: number;
        status: string;
        created_at: string;
        completed_at?: string;
        booking_count: number;
        period_start?: string;
        period_end?: string;
        transfer_code?: string;
        failure_reason?: string;
        bookings: Array<{
          id: number;
          booking_reference: string;
          booking_date: string;
          venue_share: number;
          customer_name: string;
          court_name: string;
        }>;
      }>;
    };
    revenue_by_source: {
      spotts: {
        revenue: number;
        bookings: number;
      };
      manual: {
        revenue: number;
        bookings: number;
      };
      recurring: {
        revenue: number;
        bookings: number;
      };
      memberships: {
        revenue: number;
        bookings: number;
      };
    };
    revenue_by_sport: any[];
    peak_hours: any[];
    revenue_trend: {
      daily: any[];
      weekly: any[];
      monthly: any[];
    };
    user?: {
      bank_name: string | null;
      bank_account_number_masked: string | null;
      bank_account_name: string | null;
    };
  }> {
    const params = source !== 'all' ? { source } : {};
    const response = await this.api.get<ApiResponse<any>>(
      `/vendor/venues/${venueId}/earnings-summary`,
      { params }
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to fetch earnings summary');
  }

  // Utility method to get stored user data

  // Payout methods
  async requestPayout(venueId: number): Promise<{
    success: boolean;
    message: string;
    data?: {
      payout_id: number;
      amount: number;
      status: string;
      transfer_reference: string;
      estimated_arrival: string;
    };
  }> {
    const response = await this.api.post<any>(
      `/vendor/venues/${venueId}/request-payout`
    );

    return response.data;
  }

  async getEligibleBookings(venueId: number): Promise<any[]> {
    const response = await this.api.get<any>(
      `/vendor/venues/${venueId}/bookings/eligible`
    );

    if (response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to fetch eligible bookings');
  }

  async getHeldBookings(venueId: number): Promise<any[]> {
    const response = await this.api.get<any>(
      `/vendor/venues/${venueId}/bookings/held`
    );

    if (response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to fetch held bookings');
  }

  // ==================== TEAM / STAFF MANAGEMENT (owner-only) ====================

  async getStaff(venueId: number): Promise<{ staff: any[]; staff_management_enabled: boolean }> {
    const response = await this.api.get<ApiResponse<any>>(`/vendor/venues/${venueId}/staff`);
    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'Failed to fetch team');
  }

  async getStaffCapabilities(venueId: number): Promise<{ capabilities: string[]; presets: Record<string, string[]> }> {
    const response = await this.api.get<ApiResponse<any>>(`/vendor/venues/${venueId}/staff/capabilities`);
    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'Failed to fetch capabilities');
  }

  async createStaff(venueId: number, data: {
    name: string; email: string; phone?: string; role_label?: string; capabilities: string[];
  }): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>(`/vendor/venues/${venueId}/staff`, data);
    if (response.data.success) return response.data;
    throw new Error(response.data.message || 'Failed to add staff member');
  }

  async updateStaff(venueId: number, staffId: number, data: {
    role_label?: string | null; capabilities?: string[];
  }): Promise<any> {
    const response = await this.api.put<ApiResponse<any>>(`/vendor/venues/${venueId}/staff/${staffId}`, data);
    if (response.data.success) return response.data;
    throw new Error(response.data.message || 'Failed to update staff member');
  }

  async deactivateStaff(venueId: number, staffId: number): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>(`/vendor/venues/${venueId}/staff/${staffId}/deactivate`);
    if (response.data.success) return response.data;
    throw new Error(response.data.message || 'Failed to deactivate staff member');
  }

  async reactivateStaff(venueId: number, staffId: number): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>(`/vendor/venues/${venueId}/staff/${staffId}/reactivate`);
    if (response.data.success) return response.data;
    throw new Error(response.data.message || 'Failed to reactivate staff member');
  }

  async resetStaffPassword(venueId: number, staffId: number): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>(`/vendor/venues/${venueId}/staff/${staffId}/reset-password`);
    if (response.data.success) return response.data;
    throw new Error(response.data.message || 'Failed to reset password');
  }

  // ==================== ACTIVITY LOG (activity.view) ====================

  async getVenueActivity(venueId: number, params: {
    user_id?: number; action_type?: string; from?: string; to?: string; page?: number; per_page?: number;
    // Per-booking history (booking modals) — pass exactly one of these
    booking_id?: number; recurring_booking_id?: number; recurring_group_id?: string;
  } = {}): Promise<{ data: any[]; pagination: { current_page: number; last_page: number; per_page: number; total: number } }> {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') query.append(key, String(value));
    });
    const response = await this.api.get<any>(
      `/vendor/venues/${venueId}/activity${query.size ? `?${query.toString()}` : ''}`
    );
    if (response.data.success) {
      return { data: response.data.data, pagination: response.data.pagination };
    }
    throw new Error(response.data.message || 'Failed to fetch activity');
  }

  async getPeakHoursBySport(venueId: number, sportId?: number): Promise<any[]> {
    const params = new URLSearchParams();
    if (sportId) {
      params.append('sport_id', sportId.toString());
    }

    const response = await this.api.get<any>(
      `/vendor/venues/${venueId}/peak-hours${params.size ? `?${params.toString()}` : ''}`
    );

    if (response.data.success) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to fetch peak hours');
  }

  getStoredUser(): User | null {
    const userData = localStorage.getItem('vendor_user');
    return userData ? JSON.parse(userData) : null;
  }

  // Utility method to update stored user data (for cache invalidation)
  updateStoredUser(user: User): void {
    localStorage.setItem('vendor_user', JSON.stringify(user));
  }

  // Get transactions for a venue with pagination and filters
  async getTransactions(venueId: number, params?: {
    page?: number;
    per_page?: number;
    search?: string;
    status?: string;
    transaction_type?: string;
    days?: number;
  }): Promise<any> {
    const response = await this.api.get<any>(
      `/vendor/venues/${venueId}/transactions`,
      { params }
    );
    return response.data.data || { data: [], total: 0 };
  }

  // Export transactions to CSV
  async exportTransactions(venueId: number, format: 'csv' | 'pdf', params?: {
    search?: string;
    status?: string;
    transaction_type?: string;
    days?: number;
  }): Promise<any> {
    const response = await this.api.get<any>(
      `/vendor/venues/${venueId}/transactions/export`,
      {
        params: { ...params, format },
        responseType: 'blob'
      }
    );
    return response.data;
  }

  // Get payment ledger entries (immutable audit trail with running balance)
  async getLedgerEntries(venueId: number, params?: {
    page?: number;
    per_page?: number;
    type?: string; // payment, refund, payout, debt, debt_clearance
    booking_type?: string; // payment, recurring_booking_payment, gym_membership_payment (includes refunds)
    days?: number;
  }): Promise<{
    data: Array<{
      id: number;
      ledger_entry_id: string;
      entry_type: string;
      type_label: string;
      description: string;
      amount: number;
      direction: 'credit' | 'debit';
      is_credit: boolean;
      running_balance: number;
      metadata: Record<string, unknown>;
      recorded_at: string;
      recorded_at_formatted: string;
      booking_reference: string | null;
      customer_name: string | null;
    }>;
    summary: {
      current_balance: number;
      total_credits: number;
      total_debits: number;
    };
    meta: {
      total: number;
      page: number;
      per_page: number;
      last_page: number;
    };
  }> {
    const response = await this.api.get<any>(
      `/vendor/venues/${venueId}/ledger`,
      { params }
    );
    return response.data;
  }

  // Get overview data for a venue
  // source: 'all' | 'spotts' | 'manual' - filter stats by booking source
  async getOverview(venueId: number, source?: 'all' | 'spotts' | 'manual'): Promise<any> {
    const response = await this.api.get<any>(
      `/vendor/venues/${venueId}/overview`,
      { params: source ? { source } : {} }
    );
    return response.data.data || null;
  }

  // Reviews management
  async getReviews(venueId: number, params?: {
    page?: number;
    per_page?: number;
    rating?: number;
    has_reply?: boolean;
    search?: string;
  }): Promise<any> {
    const response = await this.api.get<any>(
      `/vendor/venues/${venueId}/reviews`,
      { params }
    );
    return response.data.data || { reviews: [], pagination: {}, stats: {} };
  }

  async getReviewOverview(venueId: number): Promise<any> {
    const response = await this.api.get<any>(
      `/vendor/venues/${venueId}/reviews/overview`
    );
    return response.data.data || null;
  }

  async replyToReview(venueId: number, reviewId: number, reply: string): Promise<any> {
    const response = await this.api.post<any>(
      `/vendor/venues/${venueId}/reviews/${reviewId}/reply`,
      { reply }
    );
    return response.data;
  }

  async updateReply(venueId: number, reviewId: number, reply: string): Promise<any> {
    const response = await this.api.put<any>(
      `/vendor/venues/${venueId}/reviews/${reviewId}/reply`,
      { reply }
    );
    return response.data;
  }

  async deleteReply(venueId: number, reviewId: number): Promise<any> {
    const response = await this.api.delete<any>(
      `/vendor/venues/${venueId}/reviews/${reviewId}/reply`
    );
    return response.data;
  }

  // Account deletion
  async requestAccountDeletion(data: { reason: string }): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>(
      '/vendor/request-account-deletion',
      data
    );

    if (response.data.success) {
      return response.data;
    }

    throw new Error(response.data.message || 'Failed to request account deletion');
  }

  // Vendor promo-code management SUSPENDED Jul 2026 (owner decision):
  // promo codes are super-admin only for now; the backend routes are
  // commented out. Rebuild this section properly when re-activating.

  // Customer Insights
  async getCustomerInsights(
    venueId: number,
    period: 'week' | 'month' | 'quarter' | 'year' = 'month',
    source: 'all' | 'spotts' | 'manual' = 'all'
  ): Promise<any> {
    const response = await this.api.get<ApiResponse<any>>(
      `/vendor/venues/${venueId}/customer-insights`,
      { params: { period, source } }
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to fetch customer insights');
  }

  // ========================================================================
  // VENDOR DEBT MANAGEMENT (Phase 3: Refund System)
  // ========================================================================

  /**
   * Get vendor debt summary
   */
  async getDebtSummary(): Promise<any> {
    const response = await this.api.get<ApiResponse<any>>(`/vendor/debts/summary`);

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to fetch debt summary');
  }

  /**
   * Get all debts for vendor
   */
  async getDebts(filters?: {
    status?: string;
    date_from?: string;
    date_to?: string;
    venue_id?: number;
  }): Promise<any> {
    const response = await this.api.get<ApiResponse<any>>(`/vendor/debts`, {
      params: filters
    });

    if (response.data.success) {
      return response.data;
    }

    throw new Error(response.data.message || 'Failed to fetch debts');
  }

  /**
   * Get debt details by ID
   */
  async getDebtDetails(debtId: number): Promise<any> {
    const response = await this.api.get<ApiResponse<any>>(`/vendor/debts/${debtId}`);

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to fetch debt details');
  }

  /**
   * Dispute a debt
   */
  async disputeDebt(debtId: number, reason: string): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>(
      `/vendor/debts/${debtId}/dispute`,
      { reason }
    );

    if (response.data.success) {
      return response.data;
    }

    throw new Error(response.data.message || 'Failed to dispute debt');
  }

  /**
   * Get payout projection (with debt deductions)
   */
  async getPayoutProjection(venueId?: number): Promise<any> {
    const params = venueId ? { venue_id: venueId } : undefined;
    const response = await this.api.get<ApiResponse<any>>(
      `/vendor/debts/payout-projection`,
      { params }
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to fetch payout projection');
  }

  // ============================================================================
  // Vendor Refund Methods
  // ============================================================================

  /**
   * Get all refund requests for vendor's venues
   * @param filters - Optional filters (status, paystack_status, date_from, date_to, venue_id)
   */
  async getRefunds(filters?: {
    status?: string;
    paystack_status?: string;
    date_from?: string;
    date_to?: string;
    venue_id?: number;
  }): Promise<any> {
    const response = await this.api.get<ApiResponse<any>>(
      `/vendor/refunds`,
      { params: filters }
    );

    if (response.data.success) {
      return response.data;
    }

    throw new Error(response.data.message || 'Failed to fetch refunds');
  }

  /**
   * Get detailed information about a specific refund
   * @param refundId - Refund ID
   */
  async getRefundDetails(refundId: number): Promise<any> {
    const response = await this.api.get<ApiResponse<any>>(
      `/vendor/refunds/${refundId}`
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to fetch refund details');
  }

  /**
   * Retry a retriable refund
   */
  async retryRefund(refundId: number): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>(
      `/vendor/refunds/${refundId}/retry`
    );

    if (response.data.success) {
      return response.data;
    }

    throw new Error(response.data.message || 'Failed to retry refund');
  }

  // ========== BALANCE MANAGEMENT ==========

  /**
   * Get vendor balance overview
   * Returns: total balance, held for bookings, reserved for refunds, available for payout
   */
  async getBalanceOverview(): Promise<any> {
    const response = await this.api.get<ApiResponse<any>>(
      `/vendor/balance/overview`
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to fetch balance overview');
  }

  /**
   * Get active balance reservations
   */
  async getBalanceReservations(): Promise<any> {
    const response = await this.api.get<ApiResponse<any>>(
      `/vendor/balance/reservations`
    );

    if (response.data.success) {
      return response.data;
    }

    throw new Error(response.data.message || 'Failed to fetch reservations');
  }

  /**
   * Get balance activity timeline
   */
  async getBalanceActivity(limit = 20): Promise<any> {
    const response = await this.api.get<ApiResponse<any>>(
      `/vendor/balance/activity?limit=${limit}`
    );

    if (response.data.success) {
      return response.data;
    }

    throw new Error(response.data.message || 'Failed to fetch balance activity');
  }

  /**
   * Check if vendor can request payout
   */
  async canRequestPayout(amount: number): Promise<any> {
    const response = await this.api.post<ApiResponse<any>>(
      `/vendor/balance/can-payout`,
      { amount }
    );

    if (response.data.success) {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to check payout eligibility');
  }

  /**
   * Get transaction history (bank statement view)
   */
  async getTransactionHistory(queryParams: string = ''): Promise<any> {
    const response = await this.api.get<ApiResponse<any>>(
      `/vendor/balance/transaction-history?${queryParams}`
    );

    if (response.data.success) {
      return response.data;
    }

    throw new Error(response.data.message || 'Failed to fetch transaction history');
  }

  async exportTransactionHistory(queryParams: string = ''): Promise<any> {
    const response = await this.api.get<any>(
      `/vendor/balance/export-history?${queryParams}`,
      {
        responseType: 'blob'
      }
    );

    return response.data;
  }

  // ============================================
  // MEMBERSHIP PACKAGES (NEW FLEXIBLE SYSTEM)
  // ============================================

  /**
   * Get all membership packages for a venue
   */
  async getMembershipPackages(venueSlug: string): Promise<any> {
    const response = await this.api.get<any>(
      `/vendor/venues/${venueSlug}/packages`
    );

    if (response.data.status === 'success') {
      return response.data;
    }

    throw new Error(response.data.message || 'Failed to fetch membership packages');
  }

  /**
   * Get membership package statistics
   */
  async getPackageStats(venueSlug: string): Promise<any> {
    const response = await this.api.get<any>(
      `/vendor/venues/${venueSlug}/packages/stats`
    );

    if (response.data.status === 'success') {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to fetch package statistics');
  }

  /**
   * Create a new membership package
   */
  async createMembershipPackage(venueSlug: string, packageData: any): Promise<any> {
    const response = await this.api.post<any>(
      `/vendor/venues/${venueSlug}/packages`,
      packageData
    );

    if (response.data.status === 'success') {
      return response.data;
    }

    throw new Error(response.data.message || 'Failed to create membership package');
  }

  /**
   * Update an existing membership package
   */
  async updateMembershipPackage(venueSlug: string, packageId: number, packageData: any): Promise<any> {
    const response = await this.api.put<any>(
      `/vendor/venues/${venueSlug}/packages/${packageId}`,
      packageData
    );

    if (response.data.status === 'success') {
      return response.data;
    }

    throw new Error(response.data.message || 'Failed to update membership package');
  }

  /**
   * Toggle package active status
   */
  async togglePackageActive(venueSlug: string, packageId: number): Promise<any> {
    const response = await this.api.patch<any>(
      `/vendor/venues/${venueSlug}/packages/${packageId}/toggle`
    );

    if (response.data.status === 'success') {
      return response.data;
    }

    throw new Error(response.data.message || 'Failed to toggle package status');
  }

  /**
   * Delete a membership package
   */
  async deleteMembershipPackage(venueSlug: string, packageId: number): Promise<any> {
    const response = await this.api.delete<any>(
      `/vendor/venues/${venueSlug}/packages/${packageId}`
    );

    if (response.data.status === 'success') {
      return response.data;
    }

    throw new Error(response.data.message || 'Failed to delete membership package');
  }

  /**
   * Duplicate a membership package
   */
  async duplicateMembershipPackage(venueSlug: string, packageId: number, newName?: string): Promise<any> {
    const response = await this.api.post<any>(
      `/vendor/venues/${venueSlug}/packages/${packageId}/duplicate`,
      { package_name: newName }
    );

    if (response.data.status === 'success') {
      return response.data;
    }

    throw new Error(response.data.message || 'Failed to duplicate membership package');
  }

  /**
   * Reorder membership packages
   */
  async reorderPackages(venueSlug: string, packageIds: number[]): Promise<any> {
    const response = await this.api.post<any>(
      `/vendor/venues/${venueSlug}/packages/reorder`,
      { package_ids: packageIds }
    );

    if (response.data.status === 'success') {
      return response.data;
    }

    throw new Error(response.data.message || 'Failed to reorder packages');
  }

  // ==================== VENUE MEMBERS ====================

  async getVenueMembers(venueSlug: string, params?: {
    status?: string;
    search?: string;
    sort?: string;
    page?: number;
    per_page?: number;
  }): Promise<any> {
    const response = await this.api.get(`/vendor/venues/${venueSlug}/members`, { params });
    return response.data;
  }

  async getVenueMemberStats(venueSlug: string): Promise<any> {
    const response = await this.api.get(`/vendor/venues/${venueSlug}/members/stats`);
    return response.data;
  }

  async addManualMember(venueSlug: string, data: Record<string, any>): Promise<any> {
    const response = await this.api.post(`/vendor/venues/${venueSlug}/members`, data);
    return response.data;
  }

  async sendMemberReminder(venueSlug: string, membershipId: number): Promise<any> {
    const response = await this.api.post(`/vendor/venues/${venueSlug}/members/${membershipId}/send-reminder`);
    return response.data;
  }

  /**
   * Get member detail with membership history
   * @param venueSlug - Venue slug
   * @param userId - User ID (not membership ID)
   */
  async getMemberDetail(venueSlug: string, userId: number): Promise<any> {
    const response = await this.api.get(`/vendor/venues/${venueSlug}/members/${userId}/detail`);

    if (response.data.status === 'success') {
      return response.data.data;
    }

    throw new Error(response.data.message || 'Failed to fetch member details');
  }

  /**
   * Renew a member's membership
   * Creates a new membership record (preserves history).
   * Smart start date: if current membership active, starts after it ends.
   *
   * @param venueSlug - Venue slug
   * @param userId - User ID (not membership ID)
   * @param data - Renewal data
   */
  async renewMembership(venueSlug: string, userId: number, data: {
    package_id: number;
    payment_method: 'cash' | 'transfer' | 'pos';
    price?: number;
    notes?: string;
  }): Promise<any> {
    const response = await this.api.post(`/vendor/venues/${venueSlug}/members/${userId}/renew`, data);

    if (response.data.status === 'success') {
      return response.data;
    }

    throw new Error(response.data.message || 'Failed to renew membership');
  }

  /**
   * Cancel a membership
   * @param venueSlug - Venue slug
   * @param membershipId - Membership ID
   * @param data - Cancellation data
   */
  async cancelMembership(venueSlug: string, membershipId: number, data: {
    reason: string;
    process_refund?: boolean;
  }): Promise<any> {
    const response = await this.api.post(`/vendor/venues/${venueSlug}/members/${membershipId}/cancel`, data);

    if (response.data.status === 'success') {
      return response.data;
    }

    throw new Error(response.data.message || 'Failed to cancel membership');
  }

  // ─── Trusted Customers ───────────────────────────────────────────────────────

  async getTrustedCustomers(venueId: number): Promise<any[]> {
    const response = await this.api.get(`/vendor/venues/${venueId}/trusted-customers`);
    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'Failed to fetch trusted customers');
  }

  async searchUsersForTrust(venueId: number, query: string): Promise<any[]> {
    const response = await this.api.get(`/vendor/venues/${venueId}/trusted-customers/search`, { params: { q: query } });
    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'Failed to search customers');
  }

  async addTrustedCustomer(venueId: number, data: {
    user_id: number;
    payment_deadline_hours?: number;
    max_active_reservations?: number;
    notes?: string;
  }): Promise<any> {
    const response = await this.api.post(`/vendor/venues/${venueId}/trusted-customers`, data);
    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'Failed to add trusted customer');
  }

  async updateTrustedCustomer(id: number, data: {
    payment_deadline_hours?: number;
    max_active_reservations?: number;
    notes?: string;
    is_active?: boolean;
  }): Promise<any> {
    const response = await this.api.patch(`/vendor/trusted-customers/${id}`, data);
    if (response.data.success) return response.data.data;
    throw new Error(response.data.message || 'Failed to update trusted customer');
  }

  async removeTrustedCustomer(id: number): Promise<void> {
    const response = await this.api.delete(`/vendor/trusted-customers/${id}`);
    if (!response.data.success) throw new Error(response.data.message || 'Failed to remove trusted customer');
  }
}

// Create and export a singleton instance
export const vendorApi = new VendorApiService();
export default vendorApi;